import React, { useState, useEffect, useCallback } from 'react';
import {
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  Avatar,
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Star,
  LocalDrink,
  ScreenShare,
  ReviewsRounded,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useSnackbar } from 'notistack';
import { 
  getPendingPurchases, 
  approvePurchase, 
  denyPurchase,
} from 'lib/purchaseUtils';

const PendingPurchases = ({ user }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [pendingPurchases, setPendingPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, purchase: null, action: null });

  const loadPendingPurchases = useCallback(async () => {
    try {
      const purchases = await getPendingPurchases();
      setPendingPurchases(purchases);
    } catch (error) {
      enqueueSnackbar('Error loading pending purchases', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    loadPendingPurchases();
  }, [loadPendingPurchases]);

  const handleApprove = async (purchase) => {
    setProcessing(purchase.id);
    try {
      // Approve the purchase (points already deducted at purchase time)
      await approvePurchase(purchase.id, user.uid);
      
      enqueueSnackbar(`Approved ${purchase.itemName} for ${purchase.userName}! (Points were already deducted)`, {
        variant: 'success',
        autoHideDuration: 4000,
      });
      
      // Remove from pending list
      setPendingPurchases(prev => prev.filter(p => p.id !== purchase.id));
    } catch (error) {
      enqueueSnackbar('Error approving purchase', { variant: 'error' });
    } finally {
      setProcessing(null);
      setConfirmDialog({ open: false, purchase: null, action: null });
    }
  };

  const handleDeny = async (purchase) => {
    setProcessing(purchase.id);
    try {
      await denyPurchase(purchase.id, user.uid);
      
      enqueueSnackbar(`Denied ${purchase.itemName} for ${purchase.userName}. Points have been refunded.`, {
        variant: 'info',
        autoHideDuration: 4000,
      });
      
      // Remove from pending list
      setPendingPurchases(prev => prev.filter(p => p.id !== purchase.id));
    } catch (error) {
      enqueueSnackbar('Error denying purchase', { variant: 'error' });
    } finally {
      setProcessing(null);
      setConfirmDialog({ open: false, purchase: null, action: null });
    }
  };

  const openConfirmDialog = (purchase, action) => {
    setConfirmDialog({ open: true, purchase, action });
  };

  const getItemIcon = (itemName) => {
    if (itemName.toLowerCase().includes('slushie')) return LocalDrink;
    if (itemName.toLowerCase().includes('screen')) return ScreenShare;
    return Star;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown date';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          bgcolor: '#f8f9fa',
          borderRadius: 4,
          p: 4,
          minHeight: '60vh'
        }}
      >
        {/* Header */}
        <Box
          sx={{
            mb: 4,
            pb: 3,
            borderBottom: '1px solid #e0e0e0'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <ReviewsRounded sx={{ color: '#666', fontSize: 28 }} />
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 'bold', 
                color: '#333',
                fontFamily: '"Roboto", sans-serif'
              }}
            >
              Pending Purchases
            </Typography>
          </Box>
          <Typography 
            variant="body1" 
            sx={{ 
              color: '#666',
              fontSize: '1rem',
              opacity: 0.8
            }}
          >
            Review and approve purchase requests
          </Typography>
        </Box>

        {/* Pending Purchases Content */}
        {pendingPurchases.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Card
              sx={{
                p: 6,
                textAlign: 'center',
                borderRadius: 4,
                background: 'white',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                border: '1px solid rgba(0, 0, 0, 0.05)',
              }}
            >
              <CheckCircle sx={{ fontSize: 64, color: '#4caf50', mb: 2 }} />
              <Typography variant="h5" color="text.secondary" gutterBottom>
                All caught up!
              </Typography>
              <Typography variant="body1" color="text.secondary">
                No pending purchases to review.
              </Typography>
            </Card>
          </motion.div>
        ) : (
          <Grid container spacing={3}>
            {pendingPurchases.map((purchase, index) => {
              const IconComponent = getItemIcon(purchase.itemName);
              const isProcessing = processing === purchase.id;

              return (
                <Grid item xs={12} md={6} lg={4} key={purchase.id}>
                  <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    whileHover={{ scale: 1.02 }}
                  >
                    <Card
                      sx={{
                        height: '100%',
                        borderRadius: 3,
                        overflow: 'hidden',
                        background: 'white',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                        border: '1px solid rgba(0, 0, 0, 0.05)',
                        transition: 'all 0.3s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
                        }
                      }}
                    >
                    <CardContent sx={{ p: 3 }}>
                      {/* User Info */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          mb: 3,
                          p: 2,
                          bgcolor: '#e3f2fd',
                          borderRadius: 2,
                        }}
                      >
                        <Avatar
                          sx={{
                            bgcolor: '#1976d2',
                            color: 'white',
                            mr: 2,
                          }}
                        >
                          {purchase.userName ? purchase.userName.charAt(0).toUpperCase() : '?'}
                        </Avatar>
                        <Box>
                          <Typography variant="h6" fontWeight="bold">
                            {purchase.userName || 'Unknown User'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(purchase.createdAt)}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Item Info */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          mb: 3,
                        }}
                      >
                        <Avatar
                          sx={{
                            bgcolor: '#e3f2fd',
                            color: '#1976d2',
                            mr: 2,
                          }}
                        >
                          <IconComponent />
                        </Avatar>
                        <Box>
                          <Typography variant="h6" fontWeight="bold">
                            {purchase.itemName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {purchase.itemDescription}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Price */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          p: 2,
                          bgcolor: '#f8f9fa',
                          borderRadius: 2,
                          mb: 3,
                        }}
                      >
                        <Star sx={{ color: '#ffd700', mr: 1 }} />
                        <Typography variant="h6" fontWeight="bold">
                          {purchase.itemPrice} Points
                        </Typography>
                      </Box>

                      {/* Action Buttons */}
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Button
                            variant="contained"
                            fullWidth
                            color="success"
                            disabled={isProcessing}
                            startIcon={isProcessing ? <CircularProgress size={16} /> : <CheckCircle />}
                            onClick={() => openConfirmDialog(purchase, 'approve')}
                            sx={{
                              borderRadius: 2,
                              py: 1,
                              fontWeight: 'bold',
                            }}
                          >
                            {isProcessing ? 'Processing...' : 'Approve'}
                          </Button>
                        </Grid>
                        <Grid item xs={6}>
                          <Button
                            variant="contained"
                            fullWidth
                            color="error"
                            disabled={isProcessing}
                            startIcon={<Cancel />}
                            onClick={() => openConfirmDialog(purchase, 'deny')}
                            sx={{
                              borderRadius: 2,
                              py: 1,
                              fontWeight: 'bold',
                            }}
                          >
                            Deny
                          </Button>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>

    {/* Confirmation Dialog */}
    <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, purchase: null, action: null })}>
      <DialogTitle>
        {confirmDialog.action === 'approve' ? 'Approve Purchase' : 'Deny Purchase'}
      </DialogTitle>
      <DialogContent>
        <Typography>
          Are you sure you want to {confirmDialog.action} "{confirmDialog.purchase?.itemName}" for {confirmDialog.purchase?.userName}?
        </Typography>
        {confirmDialog.action === 'approve' && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {confirmDialog.purchase?.itemPrice} points were already deducted when the request was made.
          </Typography>
        )}
        {confirmDialog.action === 'deny' && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {confirmDialog.purchase?.itemPrice} points will be refunded to their account.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setConfirmDialog({ open: false, purchase: null, action: null })}>
          Cancel
        </Button>
        <Button
          onClick={() => {
            if (confirmDialog.action === 'approve') {
              handleApprove(confirmDialog.purchase);
            } else {
              handleDeny(confirmDialog.purchase);
            }
          }}
          color={confirmDialog.action === 'approve' ? 'success' : 'error'}
          variant="contained"
        >
          {confirmDialog.action === 'approve' ? 'Approve' : 'Deny'}
        </Button>
      </DialogActions>
    </Dialog>
  </>
  );
};

export default PendingPurchases;